import type { BookCastJson, CastCharacter, CastNarrator } from "@/lib/castAnalysisTypes";

const HONORIFIC = /^(?:mr|mrs|miss|ms|dr)\.?\s+/i;

/** Remove leading honorifics (possibly chained). */
export function stripLeadingHonorifics(s: string): string {
  let t = s.trim();
  let prev: string;
  do {
    prev = t;
    t = t.replace(HONORIFIC, "").trim();
  } while (t !== prev);
  return t;
}

function words(s: string): string[] {
  return stripLeadingHonorifics(s)
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);
}

function lastTokenLower(s: string): string {
  const w = words(s);
  return w.length ? w[w.length - 1]!.toLowerCase() : "";
}

function ambiguousSurnameHolders(pool: readonly string[], tokenLower: string): number {
  if (!tokenLower) return 0;
  let n = 0;
  for (const p of pool) {
    if (words(p).length <= 1) continue;
    if (lastTokenLower(p) === tokenLower) n += 1;
  }
  return n;
}

/** Same surface identity for clustering name + aliases within one row. */
export function samePersonSurface(a: string, b: string, pool: readonly string[]): boolean {
  const sa = stripLeadingHonorifics(a).toLowerCase();
  const sb = stripLeadingHonorifics(b).toLowerCase();
  if (!sa || !sb) return false;
  if (sa === sb) return true;
  const wa = sa.split(/\s+/).filter(Boolean);
  const wb = sb.split(/\s+/).filter(Boolean);
  if (wa.length === 1 && wb.length > 1) {
    const tok = wa[0]!;
    if (lastTokenLower(b) !== tok) return false;
    return ambiguousSurnameHolders(pool, tok) <= 1;
  }
  if (wb.length === 1 && wa.length > 1) {
    const tok = wb[0]!;
    if (lastTokenLower(a) !== tok) return false;
    return ambiguousSurnameHolders(pool, tok) <= 1;
  }
  return false;
}

function ufFind(parent: number[], i: number): number {
  if (parent[i] !== i) parent[i] = ufFind(parent, parent[i]!);
  return parent[i]!;
}

function ufUnion(parent: number[], a: number, b: number): void {
  const pa = ufFind(parent, a);
  const pb = ufFind(parent, b);
  if (pa !== pb) parent[pb] = pa;
}

function scorePrimaryCandidate(s: string): [number, number, number] {
  const stripped = stripLeadingHonorifics(s);
  const w = stripped.split(/\s+/).filter(Boolean);
  const honorificPenalty = stripped.length < s.trim().length ? 0 : 1;
  return [w.length, stripped.length, honorificPenalty];
}

function pickPrimaryFromCluster(cluster: string[]): string {
  let best = cluster[0]!;
  let bestScore = scorePrimaryCandidate(best);
  for (let i = 1; i < cluster.length; i++) {
    const c = cluster[i]!;
    const sc = scorePrimaryCandidate(c);
    if (sc[0] > bestScore[0]) {
      best = c;
      bestScore = sc;
    } else if (sc[0] === bestScore[0]) {
      if (sc[1] > bestScore[1]) {
        best = c;
        bestScore = sc;
      } else if (sc[1] === bestScore[1] && sc[2] > bestScore[2]) {
        best = c;
        bestScore = sc;
      }
    }
  }
  return best.trim();
}

function dedupeCaseInsensitive(strings: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of strings) {
    const t = s.trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

function consolidateNameAndAliases(name: string, aliases: string[] | undefined): { name: string; aliases: string[] | undefined } {
  const rawPool = dedupeCaseInsensitive([name, ...(aliases ?? [])]);
  if (rawPool.length === 0) return { name: name.trim() || "Unnamed", aliases: undefined };
  if (rawPool.length === 1) {
    const only = rawPool[0]!;
    return { name: only, aliases: undefined };
  }

  const n = rawPool.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (samePersonSurface(rawPool[i]!, rawPool[j]!, rawPool)) ufUnion(parent, i, j);
    }
  }

  const clusters = new Map<number, string[]>();
  for (let i = 0; i < n; i++) {
    const r = ufFind(parent, i);
    const arr = clusters.get(r) ?? [];
    arr.push(rawPool[i]!);
    clusters.set(r, arr);
  }
  const groupList = [...clusters.values()];

  const nameLower = name.trim().toLowerCase();
  const mainCluster =
    groupList.find((g) => g.some((s) => s.trim().toLowerCase() === nameLower)) ??
    groupList.reduce((best, g) => (g.length > best.length ? g : best), groupList[0]!);

  const primary = pickPrimaryFromCluster(mainCluster);
  const aliasParts: string[] = [];
  for (const g of groupList) {
    for (const s of g) {
      const t = s.trim();
      if (!t) continue;
      if (t.toLowerCase() === primary.toLowerCase()) continue;
      aliasParts.push(t);
    }
  }
  const aliasesOut = dedupeCaseInsensitive(aliasParts);
  return { name: primary, aliases: aliasesOut.length > 0 ? aliasesOut.slice(0, 28) : undefined };
}

function cleanNarratorName(n: CastNarrator): CastNarrator {
  const stripped = stripLeadingHonorifics(n.name);
  const name = stripped.length > 0 ? stripped : n.name;
  return { ...n, name };
}

function cleanCharacterRow(c: CastCharacter): CastCharacter {
  const { name, aliases } = consolidateNameAndAliases(c.name, c.aliases);
  return { ...c, name, aliases };
}

/**
 * Client-side pass after merge / normalize: honorific stripping, surname-aware dedupe of
 * name + aliases within each row. Preserves non-duplicate surface forms as aliases.
 */
export function applyCastAliasCleanupPass(doc: BookCastJson): BookCastJson {
  if (!doc.success || doc.status !== "ready") return doc;
  const narrator = cleanNarratorName(doc.narrator);
  const characters = (doc.characters ?? []).map((c) => cleanCharacterRow(c));
  return { ...doc, narrator, characters };
}
